import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';

export function Pagination(paginationProps: ComponentProps<'nav'>) {
  const { className, ...props } = paginationProps;
  return <nav role="navigation" aria-label="pagination" data-slot="pagination" className={cn('mx-auto flex w-full justify-center', className)} {...props} />;
}

export function PaginationContent(paginationContentProps: ComponentProps<'ul'>) {
  const { className, ...props } = paginationContentProps;
  return <ul data-slot="pagination-content" className={cn('gap-0.5 flex items-center', className)} {...props} />;
}

export function PaginationItem(paginationItemProps: ComponentProps<'li'>) {
  const { className, ...props } = paginationItemProps;

  return <li data-slot="pagination-item" className={className} {...props} />;
}

export type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ComponentProps<typeof Button>, 'size'> &
  ComponentProps<'a'>;

export function PaginationLink(paginationLinkProps: PaginationLinkProps) {
  const { className, isActive, size = 'sm', ...props } = paginationLinkProps;
  return (
    <Button asChild variant={isActive ? 'outline' : 'ghost'} size={size} className={cn(className)}>
      <a aria-current={isActive ? 'page' : undefined} data-slot="pagination-link" data-active={isActive} {...props} />
    </Button>
  );
}

export function PaginationPrevious(paginationPreviousProps: ComponentProps<typeof PaginationLink> & { text?: string }) {
  const { className, text = 'Previous', ...props } = paginationPreviousProps;
  return (
    <PaginationLink aria-label="Go to previous page" size="sm" className={cn('pl-1.5!', className)} {...props}>
      <ChevronLeftIcon data-icon="inline-start" className="cn-rtl-flip" />
      <span className="hidden sm:block">{text}</span>
    </PaginationLink>
  );
}

export function PaginationNext(paginationNextProps: ComponentProps<typeof PaginationLink> & { text?: string }) {
  const { className, text = 'Next', ...props } = paginationNextProps;
  return (
    <PaginationLink aria-label="Go to next page" size="sm" className={cn('pr-1.5!', className)} {...props}>
      <span className="hidden sm:block">{text}</span>
      <ChevronRightIcon data-icon="inline-end" className="cn-rtl-flip" />
    </PaginationLink>
  );
}

export function PaginationEllipsis(paginationEllipsisProps: ComponentProps<'span'>) {
  const { className, ...props } = paginationEllipsisProps;
  return (
    <span aria-hidden data-slot="pagination-ellipsis" className={cn("size-8 [&_svg:not([class*='size-'])]:size-4 flex items-center justify-center", className)} {...props}>
      <MoreHorizontalIcon />
      <span className="sr-only">More pages</span>
    </span>
  );
}
